import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/*
 * Not used here, and extended on purpose.
 *
 * `dayjs('09:00', 'HH:mm')` returns Invalid Date without this plugin. Anywhere
 * that parses a stored wall clock has been working only because
 * `@rc-component/picker` extends it onto the global dayjs as an import side
 * effect — and that module now says it is deliberately moving to an internal
 * instance to stop leaking its plugins. This file is imported app-wide through
 * `format.ts`, so claiming the dependency here makes it ours.
 */
dayjs.extend(customParseFormat);

/**
 * One clock for the whole counter app.
 *
 * Every date here was rendered with plain `dayjs(value).format(...)` — the
 * timezone of whatever machine the till is running on. For a chain trading in
 * one country that is not a nuance:
 *
 *   A movement stamped 03:23 means one thing to the shop and another to head
 *   office if their machines disagree, with nothing on screen admitting it.
 *
 *   Wall clocks picked in a form were serialised with `toISOString()`, which
 *   converts through the BROWSER's zone — so a date typed at the counter could
 *   reach the server as the day before.
 *
 *   `daysUntil` snapped `startOf('day')` on the local clock, which for an
 *   expiry warning is the difference between sellable and not.
 *
 * This must agree with the server's `PLATFORM_TIMEZONE` environment variable —
 * that is what the backend compares a shop's `09:00` opening time against. If
 * the two ever differ, this app will show one story and the marketplace will
 * act on another.
 */
export const PLATFORM_TIMEZONE = 'Asia/Kolkata';

let current: string = PLATFORM_TIMEZONE;

export function platformTimezone(): string {
  return current;
}

/** Here for the day the zone is served from config rather than compiled in. */
export function setPlatformTimezone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
  } catch {
    return false;
  }
  current = zone;
  return true;
}

/** `UTC+05:30` — abbreviations like IST are not unique, so the offset is shown. */
export function zoneOffsetLabel(zone: string = current): string {
  const minutes = dayjs().tz(zone).utcOffset();
  const sign = minutes < 0 ? '-' : '+';
  const absolute = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(
    absolute % 60,
  ).padStart(2, '0')}`;
}

/** An instant from the server, read on the platform's clock. */
export function inZone(value?: string | number | Date | Dayjs | null): Dayjs {
  return dayjs(value ?? undefined).tz(current);
}

/** Now, on the platform's clock. */
export function nowInZone(): Dayjs {
  return dayjs().tz(current);
}

/**
 * A wall clock somebody picked, resolved to the instant it names HERE.
 *
 * antd's pickers build their `Dayjs` on the browser's zone, so only the face of
 * the clock is worth keeping. This re-reads those digits as platform-local.
 */
export function wallClockToInstant(picked: Dayjs, zone: string = current): Dayjs {
  return dayjs.tz(picked.format('YYYY-MM-DD HH:mm:ss'), zone);
}

/**
 * The day goods arrived, as the instant to send.
 *
 * Today means now: the delivery is in front of you. Any other day means the
 * start of that day here, since only the date is known and the start of it is
 * the one time that can never land after the moment it is booked.
 */
export function arrivalDayToInstant(picked: Dayjs, zone: string = current): Dayjs {
  const day = picked.format('YYYY-MM-DD');
  if (day === dayjs().tz(zone).format('YYYY-MM-DD')) return dayjs();
  return dayjs.tz(day, zone);
}

/** For a date picker that must not offer a day after today on the platform's clock. */
export function isAfterTodayHere(picked: Dayjs, zone: string = current): boolean {
  return picked.format('YYYY-MM-DD') > dayjs().tz(zone).format('YYYY-MM-DD');
}

/** The reverse, for loading a stored instant back into a picker unchanged. */
export function instantToWallClock(value: string | Dayjs, zone: string = current): Dayjs {
  return dayjs(dayjs(value).tz(zone).format('YYYY-MM-DD HH:mm:ss'));
}
